import {
  AuthContainer,
  AuthHeader,
  AuthErrorBanner,
  AuthFormField,
  AuthPasswordField,
  AuthSubmitButton,
  AuthLink,
  AuthDivider,
  AuthOAuthProviders,
  AuthEmailVerificationStep,
} from '../atoms';
import type { OAuthProvidersSchema } from '@insforge/shared-schemas';
import type { FormEvent } from 'react';
import type { AuthConfig } from '../../types';
import { theme } from '../../styles/theme';

export interface SignInFormProps {
  email: string;
  password: string;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  error?: string;
  loading?: boolean;
  oauthLoading?: OAuthProvidersSchema | null;
  onOAuthClick?: (provider: OAuthProvidersSchema) => void;
  authConfig: AuthConfig;
  title?: string;
  subtitle?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  passwordLabel?: string;
  passwordPlaceholder?: string;
  forgotPasswordText?: string;
  forgotPasswordUrl?: string;
  submitButtonText?: string;
  loadingButtonText?: string;
  signUpText?: string;
  signUpLinkText?: string;
  signUpUrl?: string;
  dividerText?: string;
  showVerificationStep?: boolean;
  onVerifyCode?: (code: string) => Promise<void>;
}

/**
 * Pre-built sign-in form component (UI only, no business logic).
 */
export function SignInForm({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  error,
  loading = false,
  oauthLoading = null,
  onOAuthClick,
  authConfig,
  title = 'Welcome Back',
  subtitle = 'Login to your account',
  emailLabel = 'Email',
  emailPlaceholder = 'example@email.com',
  passwordLabel = 'Password',
  passwordPlaceholder = '••••••',
  forgotPasswordText = 'Forget Password?',
  forgotPasswordUrl = '/forgot-password',
  submitButtonText = 'Sign In',
  loadingButtonText = 'Signing in...',
  signUpText = "Don't have an account?",
  signUpLinkText = 'Sign Up Now',
  signUpUrl = '/sign-up',
  dividerText = 'or',
  showVerificationStep = false,
  onVerifyCode,
}: SignInFormProps) {
  return (
    <AuthContainer>
      <AuthHeader title={title} subtitle={subtitle} />

      <AuthErrorBanner error={error || ''} />

      {showVerificationStep ? (
        <AuthEmailVerificationStep
          email={email}
          method={authConfig.verifyEmailMethod}
          onVerifyCode={onVerifyCode}
          emailSent={false}
        />
      ) : (
        <form
          onSubmit={onSubmit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}
        >
          <AuthFormField
            id="email"
            type="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
            autoComplete="email"
          />

          <AuthPasswordField
            id="password"
            label={passwordLabel}
            placeholder={passwordPlaceholder}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            required
            autoComplete="current-password"
            authConfig={authConfig}
            forgotPasswordLink={
              forgotPasswordUrl
                ? {
                    href: forgotPasswordUrl,
                    text: forgotPasswordText,
                  }
                : undefined
            }
          />

          <AuthSubmitButton isLoading={loading} disabled={loading || oauthLoading !== null}>
            {loading ? loadingButtonText : submitButtonText}
          </AuthSubmitButton>
        </form>
      )}

      {!showVerificationStep && (
        <>
          <AuthLink text={signUpText} linkText={signUpLinkText} href={signUpUrl} />

          {authConfig.oAuthProviders.length > 0 && onOAuthClick && (
            <>
              <AuthDivider text={dividerText} />
              <AuthOAuthProviders
                providers={authConfig.oAuthProviders}
                onClick={onOAuthClick}
                disabled={loading || oauthLoading !== null}
                loading={oauthLoading}
              />
            </>
          )}
        </>
      )}
    </AuthContainer>
  );
}
